<template>
  <section class="policy-card mb-3 min-w-0">
    <div class="p-3 p-lg-4 text-start">
      <div class="row g-3">
        <div class="col-12 col-lg-6">
          <div class="rounded border bg-light p-3 h-100">
            <div class="mb-3">
              <div class="fs-8 text-gray-600 text-uppercase fw-semibold mb-3">Страхователь</div>
              <div v-if="policy.insurer" class="bg-white rounded border px-3 py-2 fs-8 text-break">
                <span class="fw-bold text-gray-800">{{ clientTitle(policy.insurer) }}</span><span v-if="clientMeta(policy.insurer)" class="text-gray-500"> · {{ clientMeta(policy.insurer) }}</span>
              </div>
              <div v-else class="bg-white rounded border px-3 py-2 fs-8 text-gray-500">—</div>
            </div>

            <div class="mb-3">
              <div class="fs-8 text-gray-600 text-uppercase fw-semibold mb-3">Водители</div>
              <template v-if="policy.insureds.length">
                <div
                  v-for="(item, key) in policy.insureds"
                  :key="'sum-insured-' + key"
                  class="bg-white rounded border px-3 py-2 fs-8 text-break"
                  :class="{ 'mb-1': key < policy.insureds.length - 1 }"
                >
                  <span class="fw-bold text-gray-800">{{ clientTitle(item) }}</span><span v-if="clientMeta(item)" class="text-gray-500"> · {{ clientMeta(item) }}</span>
                </div>
              </template>
              <div v-else class="bg-white rounded border px-3 py-2 fs-8 text-gray-500">—</div>
            </div>

            <div>
              <div class="fs-8 text-gray-600 text-uppercase fw-semibold mb-3">Транспортные средства</div>
              <template v-if="policy.vehicle.length">
                <div
                  v-for="(item, key) in policy.vehicle"
                  :key="'sum-vehicle-' + key"
                  class="bg-white rounded border px-3 py-2 fs-8 text-break"
                  :class="{ 'mb-1': key < policy.vehicle.length - 1 }"
                >
                  <span class="fw-bold text-gray-800">{{ vehicleTitle(item) }}</span><span v-if="vehicleMeta(item)" class="text-gray-500"> · {{ vehicleMeta(item) }}</span>
                </div>
              </template>
              <div v-else class="bg-white rounded border px-3 py-2 fs-8 text-gray-500">—</div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-6">
          <div class="rounded border bg-light p-3 h-100">
            <div class="fs-8 text-gray-600 text-uppercase fw-semibold mb-3">Договор</div>
            <ul class="list-unstyled mb-0">
              <li class="bg-white rounded border px-3 py-2 mb-1 d-flex flex-column flex-sm-row gap-1 gap-sm-3">
                <span class="text-gray-600 fs-8 fw-semibold col-sm-4 flex-shrink-0">Тип договора</span>
                <span class="fs-7 text-gray-800 min-w-0">{{ mode.title }}</span>
              </li>
              <li class="bg-white rounded border px-3 py-2 mb-1 d-flex flex-column flex-sm-row gap-1 gap-sm-3">
                <span class="text-gray-600 fs-8 fw-semibold col-sm-4 flex-shrink-0">Период</span>
                <span class="min-w-0">
                  <span class="fs-7 fw-bold text-gray-800">{{ formatDateRu(policy.treaty.date) }} — {{ formatDateRu(policy.treaty.term) }}</span>
                  <span v-if="durationTitle" class="fs-7 text-gray-500 ms-2">({{ durationTitle }})</span>
                </span>
              </li>
              <li class="bg-white rounded border px-3 py-2 mb-1 d-flex flex-column flex-sm-row gap-1 gap-sm-3">
                <span class="text-gray-600 fs-8 fw-semibold col-sm-4 flex-shrink-0">Менеджер</span>
                <span class="fs-7 fw-semibold text-gray-800 min-w-0">{{ managerName || '—' }}</span>
              </li>
              <li v-if="policy.dgpo" class="bg-white rounded border px-3 py-2 d-flex flex-column flex-sm-row gap-1 gap-sm-3">
                <span class="text-gray-600 fs-8 fw-semibold col-sm-4 flex-shrink-0">ДГПО ВТС +</span>
                <span class="fs-8 text-gray-800 min-w-0">{{ dgpoSummaryText }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'PolicyBpmSummary',
  props: {
    policy: {
      type: Object,
      required: true,
    },
    mode: {
      type: [Object, Function],
      required: true,
    },
    module: {
      type: Function,
      required: true,
    },
    dictionaries: {
      type: Object,
      required: true,
    },
    duration: {
      type: Object,
      default: null,
    },
  },
  computed: {
    durationTitle() {
      if (!this.duration || !this.dictionaries?.durations?.length) return ''
      const match = this.dictionaries.durations.find(item => {
        const id = item.id
        return id?.type === this.duration?.type && id?.value === this.duration?.value
      })
      return match?.title ?? ''
    },
    managerName() {
      if (!this.policy?.manager || !this.dictionaries?.managers?.length) return ''
      const match = this.dictionaries.managers.find(m => m.id === this.policy.manager)
      return match?.name ?? ''
    },
    dgpoSummaryText() {
      if (!this.policy?.dgpo) return ''
      const sumItem = this.module.Dgpo.sumDictionary().find(i => i.value === this.policy.dgpo.sum)
      const avItem = this.module.Dgpo.avDictionary().find(i => i.value === this.policy.dgpo.av)
      const parts = [`Страховая сумма: ${sumItem?.text ?? this.policy.dgpo.sum}`]
      if (avItem) parts.push(`АВ: ${avItem.text}`)
      if (this.policy.dgpo.agent) {
        const agent = this.dictionaries.agents?.find(a => a.id === this.policy.dgpo.agent)
        if (agent?.name) parts.push(`Агент: ${agent.name}`)
      }
      return parts.join(', ')
    },
  },
  methods: {
    clientTitle(clientItem) {
      if (!clientItem?.client) return '—'
      const c = clientItem.client
      return c.title || this.module.Client.clientName(c)
    },
    clientMeta(clientItem) {
      if (!clientItem?.client) return ''
      const parts = []
      const modal = this.module.Modal

      if (modal.infoCard('iin')) {
        const id = clientItem.client.iin
          ? this.formatIin(clientItem.client.iin)
          : clientItem.document?.number
        if (id) parts.push(id)
      }
      if (modal.infoCard('person')) {
        parts.push(clientItem.filter?.person ? 'ФЛ' : 'ЮЛ')
      }
      if (modal.infoCard('resident')) {
        parts.push(clientItem.filter?.resident ? 'Рез.' : 'Нерез.')
      }
      if (modal.infoCard('class') && clientItem.class?.title) {
        parts.push(`Класс: ${this.stripHtml(clientItem.class.title)}`)
      }
      if (modal.infoCard('pensioner') && clientItem.client?.pensioner) {
        parts.push('Льгота')
      }

      return parts.join(' · ')
    },
    vehicleTitle(vehicle) {
      if (!vehicle) return '—'
      return vehicle.number ?? vehicle.vin ?? '—'
    },
    vehicleMeta(vehicle) {
      if (!vehicle) return ''
      const parts = [(vehicle.mark ?? ''), (vehicle.model ?? ''), (vehicle.year ?? '')].filter(Boolean)
      const details = parts.join(' ')
      const registration = vehicle.verify ? 'Каз.учет' : 'Нерезидент'
      return details ? `${details} · ${registration}` : registration
    },
    formatIin(iin) {
      const digitsOnly = String(iin).replace(/\s+/g, '')
      return digitsOnly.replace(/(.{3})/g, '$1 ').trim()
    },
    stripHtml(value) {
      return String(value).replace(/<[^>]*>/g, '')
    },
    formatDateRu(date) {
      if (!date) {
        return ''
      }
      const d = new Date(date)
      if (Number.isNaN(d.getTime())) {
        return ''
      }
      return d.toLocaleDateString('ru-RU')
    },
  },
}
</script>
